import { FirebaseAnalytics } from '@capacitor-firebase/analytics';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { Capacitor } from '@capacitor/core';
import { getAnalytics, logEvent as webLogEvent } from 'firebase/analytics';
import { FirebaseOptions, initializeApp } from 'firebase/app';
import {
  FacebookAuthProvider,
  GoogleAuthProvider,
  OAuthProvider,
  getAuth,
  signInWithCredential,
} from 'firebase/auth';
import {
  firebaseApiKey,
  firebaseAppId,
  firebaseAuthDomain,
  firebaseDatabaseUrl,
  firebaseMeasurementId,
  firebaseMessagingSenderId,
  firebaseProjectId,
  firebaseStorageBucket,
} from 'src/config';

const firebaseConfig: FirebaseOptions = {
  apiKey: firebaseApiKey,
  authDomain: firebaseAuthDomain,
  databaseURL: firebaseDatabaseUrl,
  projectId: firebaseProjectId,
  storageBucket: firebaseStorageBucket,
  messagingSenderId: firebaseMessagingSenderId,
  appId: firebaseAppId,
  measurementId: firebaseMeasurementId,
};

const app = initializeApp(firebaseConfig);

export const googleAuthProvider = new GoogleAuthProvider();
googleAuthProvider.setCustomParameters({ prompt: 'select_account' });

export const facebookAuthProvider = new FacebookAuthProvider();

export const appleAuthProvider = new OAuthProvider('apple.com');

export const auth = getAuth();

const signInWithApple = async () => {
  // 1. Create credentials on the native layer
  const result = await FirebaseAuthentication.signInWithApple({
    skipNativeAuth: true,
  });
  // 2. Sign in on the web layer using the id token and nonce
  const provider = new OAuthProvider('apple.com');
  const credential = provider.credential({
    idToken: result.credential?.idToken,
    rawNonce: result.credential?.nonce,
  });
  const auth = getAuth();
  return await signInWithCredential(auth, credential);
};

const signInWithFacebook = async () => {
  // 1. Create credentials on the native layer
  const result = await FirebaseAuthentication.signInWithFacebook();
  // 2. Sign in on the web layer using the access token
  const credential = FacebookAuthProvider.credential(
    result.credential?.accessToken as string
  );
  const auth = getAuth();
  return await signInWithCredential(auth, credential);
};

const signInWithGoogle = async () => {
  // 1. Create credentials on the native layer
  const result = await FirebaseAuthentication.signInWithGoogle();
  // 2. Sign in on the web layer using the id token
  const credential = GoogleAuthProvider.credential(result.credential?.idToken);
  const auth = getAuth();
  return await signInWithCredential(auth, credential);
};

export const providers = {
  facebook: signInWithFacebook,
  google: signInWithGoogle,
  apple: signInWithApple,
};

const webAnalytics = getAnalytics(app);

const isMobilePlatform = Capacitor.isNativePlatform();

/**
 * Normalizes event tracking across platforms (mobile and web).
 * @param name - The name of the event.
 * @param params - Additional parameters for the event.
 */
export const trackEvent = async (
  name: string,
  params?: Record<string, unknown>
): Promise<void> => {
  if (isMobilePlatform) {
    await FirebaseAnalytics.logEvent({ name, params });
  } else {
    webLogEvent(webAnalytics, name, params);
  }
};
